import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import StudentProgressDrilldown from "../components/StudentProgressDrilldown";
import { Users, Plus, Key, ShieldCheck, Trash, Eye, Copy, CheckCircle, X, Stethoscope, PencilSimpleSlash, PencilSimple, UsersFour, DownloadSimple, Printer, Gear, ChatCircleText, ArrowsClockwise, LinkSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import Announcements from "../components/Announcements";
import ReviewQueueCard from "../components/ReviewQueueCard";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";

const ROLES = ["teacher", "assistant", "student", "parent"];
const ROLE_BADGE = {
  teacher:   "bg-yellow-100 text-yellow-900",
  assistant: "bg-purple-100 text-purple-900",
  student:   "bg-emerald-100 text-emerald-900",
  parent:    "bg-blue-100 text-blue-900",
};

function CreatedCredentials({ creds, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!creds) return null;
  const summary = `Login: ${creds.username}\nPassword: ${creds.password}`;
  const copy = async () => {
    await navigator.clipboard.writeText(summary);
    setCopied(true);
    toast.success("Credentials copied");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="created-credentials">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-bold tracking-wider uppercase text-amber-800">Account created — save these now</div>
          <div className="font-outfit font-bold text-lg mt-1">{creds.name}</div>
          <div className="text-sm text-slate-700 mt-2 space-y-0.5">
            <div><span className="font-semibold">Login:</span> <span className="font-mono bg-white px-2 py-0.5 rounded">{creds.username}</span></div>
            <div><span className="font-semibold">Password:</span> <span className="font-mono bg-white px-2 py-0.5 rounded">{creds.password}</span></div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={copy}
            data-testid="copy-credentials-btn"
            className="px-3 py-2 rounded-xl bg-white border border-amber-200 text-sm font-semibold hover:bg-amber-100 inline-flex items-center gap-1"
          >
            {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={onClose}
            data-testid="dismiss-credentials-btn"
            className="p-2 rounded-lg hover:bg-amber-100 text-amber-700"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserDialog({ open, onClose, onCreated, students }) {
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "student", linked_student_ids: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", username: "", password: "", role: "student", linked_student_ids: [] });
  }, [open]);

  const autoUsername = () => {
    const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 16);
    if (slug && !form.username) setForm((f) => ({ ...f, username: slug }));
  };
  const autoPassword = () => {
    const words = ["yam", "cocoa", "drum", "banku", "lion", "moon", "river", "sun", "kente", "star"];
    const pw = words[Math.floor(Math.random() * words.length)] + "-" + Math.floor(Math.random() * 90 + 10);
    setForm((f) => ({ ...f, password: pw }));
  };

  const toggleStudent = (id) => {
    setForm((f) => ({
      ...f,
      linked_student_ids: f.linked_student_ids.includes(id)
        ? f.linked_student_ids.filter((x) => x !== id)
        : [...f.linked_student_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        name: form.name,
        username: form.username,
        password: form.password,
        role: form.role,
        linked_student_ids: form.role === "parent" ? form.linked_student_ids : undefined,
      });
      onCreated({ ...data, password: form.password });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
      >
        <motion.form
          onSubmit={submit}
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="create-user-dialog"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-outfit font-bold text-xl">Add a new account</h3>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Full name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={autoUsername}
                data-testid="create-name-input"
                autoComplete="off"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                placeholder="Kwame Mensah"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Username (login)</label>
              <div className="flex gap-2">
                <input
                  required
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
                  data-testid="create-username-input"
                  autoComplete="off"
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono"
                  placeholder="kwame"
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">No real email needed. Lowercase letters, numbers, _ . - only.</div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Temporary password</label>
              <div className="flex gap-2">
                <input
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="create-password-input"
                  autoComplete="new-password"
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono"
                  placeholder="at least 6 characters"
                />
                <button
                  type="button"
                  onClick={autoPassword}
                  data-testid="autopassword-btn"
                  className="px-3 py-2 rounded-xl bg-slate-100 text-sm font-semibold hover:bg-slate-200"
                >
                  Suggest
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Role</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setForm({ ...form, role: r })}
                    data-testid={`create-role-${r}`}
                    className={`py-2 rounded-xl border-2 text-sm font-semibold capitalize ${
                      form.role === r ? "border-yellow-400 bg-yellow-50 text-yellow-950" : "border-slate-200 bg-white"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            {form.role === "parent" && (
              <div data-testid="parent-link-section">
                <label className="text-sm font-medium block mb-1">Link to student(s)</label>
                <div className="max-h-48 overflow-y-auto bg-white rounded-xl border border-slate-200 p-2 space-y-1">
                  {students.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">No student accounts yet. Create a student first.</div>
                  ) : students.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.linked_student_ids.includes(s.id)}
                        onChange={() => toggleStudent(s.id)}
                        data-testid={`link-student-${s.id}`}
                      />
                      <span>{s.name}</span>
                      <span className="text-xs text-slate-400 ml-auto">{s.username || "—"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="p-6 border-t border-slate-200 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-xl hover:bg-slate-100 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="create-submit-btn"
              className="btn-tactile-yellow px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500 disabled:opacity-60"
            >
              {busy ? "Creating..." : "Create account"}
            </button>
          </div>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

function BulkImportDialog({ open, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [role, setRole] = useState("student");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setText("");
      setRole("student");
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      toast.error("Add at least one name");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk", {
        users: lines.map((name) => ({ name })),
        role,
      });
      setResult(data);
      if (data.count > 0) {
        toast.success(`Created ${data.count} account${data.count === 1 ? "" : "s"}`);
        onCreated();
      }
      if (data.errors?.length) {
        toast.error(`${data.errors.length} row${data.errors.length === 1 ? "" : "s"} skipped`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!result?.created) return;
    const lines = ["Name,Username,Password,Role", ...result.created.map(
      (u) => `${u.name},${u.username},${u.password},${u.role}`
    )];
    await navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
    toast.success("All credentials copied as CSV");
    setTimeout(() => setCopied(false), 1500);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        data-testid="bulk-import-overlay"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="bulk-import-dialog"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">Bulk import</div>
              <h2 className="font-outfit text-2xl font-bold">Paste a class list</h2>
              <p className="text-xs text-slate-500 mt-1">One name per line. Usernames + passwords are auto-generated.</p>
            </div>
            <button
              onClick={onClose}
              data-testid="bulk-close-btn"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 space-y-4">
            {!result ? (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Assign role to everyone</label>
                  <div className="grid grid-cols-3 gap-2">
                    {["student", "teacher", "assistant"].map((r) => (
                      <button
                        type="button"
                        key={r}
                        onClick={() => setRole(r)}
                        data-testid={`bulk-role-${r}`}
                        className={`py-2 rounded-xl border-2 text-sm font-semibold capitalize ${
                          role === r ? "border-yellow-400 bg-yellow-50 text-yellow-950" : "border-slate-200 bg-white"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Names (one per line)</label>
                  <textarea
                    rows={10}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid="bulk-names-input"
                    placeholder={"Kwame Mensah\nAkua Boateng\nYaw Asante\n..."}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono bg-white"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    {text.split(/\r?\n/).filter((l) => l.trim()).length} name(s) detected. Max 100 per batch.
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-2 rounded-xl hover:bg-slate-100 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="bulk-submit-btn"
                    className="btn-tactile-yellow px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500 disabled:opacity-60 inline-flex items-center gap-1"
                  >
                    <UsersFour size={16} weight="bold" />
                    {busy ? "Creating..." : "Create accounts"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4" data-testid="bulk-result">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-bold tracking-wider uppercase text-emerald-800">
                        {result.count} account{result.count === 1 ? "" : "s"} created
                      </div>
                      <p className="text-sm text-emerald-900 mt-1">
                        Copy these credentials now — passwords are <strong>not</strong> retrievable later.
                      </p>
                    </div>
                    <button
                      onClick={copyAll}
                      data-testid="bulk-copy-all-btn"
                      className="px-3 py-2 rounded-xl bg-white border border-emerald-200 text-sm font-semibold hover:bg-emerald-100 inline-flex items-center gap-1 shrink-0"
                    >
                      {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
                      {copied ? "Copied" : "Copy all"}
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm" data-testid="bulk-result-table">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] font-bold tracking-wider uppercase text-slate-500">
                        <th className="px-4 py-2">Name</th>
                        <th className="px-4 py-2">Login</th>
                        <th className="px-4 py-2">Password</th>
                        <th className="px-4 py-2">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.created.map((u) => (
                        <tr key={u.id} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-medium">{u.name}</td>
                          <td className="px-4 py-2 font-mono text-xs">{u.username}</td>
                          <td className="px-4 py-2 font-mono text-xs">{u.password}</td>
                          <td className="px-4 py-2 capitalize text-xs text-slate-600">{u.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {result.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                    <div className="text-xs font-bold tracking-wider uppercase text-red-800 mb-2">
                      {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped
                    </div>
                    <ul className="text-sm text-red-900 space-y-0.5">
                      {result.errors.map((e, i) => (
                        <li key={i}>"{e.name}" — {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={onClose}
                    data-testid="bulk-done-btn"
                    className="btn-tactile-yellow px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function EditLinksDialog({ parent, students, onClose, onSaved }) {
  const [selected, setSelected] = useState(() => new Set(parent?.linked_student_ids || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelected(new Set(parent?.linked_student_ids || []));
  }, [parent]);

  if (!parent) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const linked_student_ids = Array.from(selected);
      await api.put(`/admin/users/${parent.id}`, { linked_student_ids });
      toast.success(
        linked_student_ids.length === 0
          ? "Parent now has no linked students"
          : `Linked to ${linked_student_ids.length} student${linked_student_ids.length === 1 ? "" : "s"}`,
      );
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update links");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        data-testid="edit-links-overlay"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="edit-links-dialog"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">
                Linked students
              </div>
              <h2 className="font-outfit text-xl font-bold truncate">{parent.name}</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {parent.username || parent.email}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="edit-links-close"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="overflow-y-auto p-6">
            <p className="text-sm text-slate-600 mb-3">
              Pick the children whose progress this parent should see. Linked
              children appear on the parent's dashboard with their tasks, badges
              and conversation starters.
            </p>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {students.length === 0 ? (
                <div className="p-6 text-sm text-slate-400 text-center" data-testid="edit-links-empty">
                  No student accounts yet. Create a student first.
                </div>
              ) : (
                students.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggle(s.id)}
                      data-testid={`edit-links-toggle-${s.id}`}
                      className="w-4 h-4 accent-yellow-500"
                    />
                    <img
                      src={`https://api.dicebear.com/9.x/micah/svg?seed=${encodeURIComponent(s.name)}`}
                      alt=""
                      className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">
                        {s.username || s.email}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="p-6 border-t border-slate-200 flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500" data-testid="edit-links-count">
              {selected.size === 0
                ? "No students linked"
                : `${selected.size} student${selected.size === 1 ? "" : "s"} linked`}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-3 py-2 rounded-xl hover:bg-slate-100 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy}
                data-testid="edit-links-save-btn"
                className="btn-tactile-yellow px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500 disabled:opacity-60"
              >
                {busy ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Admin() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = !!currentUser?.is_admin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [drilldownUserId, setDrilldownUserId] = useState(null);
  const [editLinksParent, setEditLinksParent] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [latestCreds, setLatestCreds] = useState(null);

  // Class settings (class name, code, exhibition date)
  const [classInfo, setClassInfo] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [classBusy, setClassBusy] = useState(false);

  // Notes for parents — separate from the class Pinboard. Posted via
  // /api/parent-notes and visible only on the public parent portal.
  const [parentNotes, setParentNotes] = useState([]);

  const loadClass = async () => {
    const { data } = await api.get("/class/info");
    setClassInfo(data);
    setNameDraft(data.class_name);
  };

  const loadNotes = async () => {
    const { data } = await api.get("/parent-notes");
    setParentNotes(data);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadClass(); loadNotes(); }, []);

  const students = useMemo(() => users.filter((u) => u.role === "student"), [users]);
  const filtered = useMemo(() => (filter === "all" ? users : users.filter((u) => u.role === filter)), [users, filter]);

  const onRoleChange = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}`, { role });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, role } : u)));
      toast.success(`Role updated to ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const resetPassword = async (user) => {
    const pw = window.prompt(`New password for ${user.name}?`, "");
    if (!pw) return;
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { password: pw });
      toast.success("Password reset");
      setLatestCreds({ name: user.name, username: user.username || user.email, password: pw });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.name}? This removes their account and all their progress data. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      setUsers((us) => us.filter((u) => u.id !== user.id));
      toast.success(`${user.name} deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account");
    }
  };

  const toggleCurriculumEdit = async (target) => {
    const next = !target.can_edit_curriculum;
    try {
      const { data } = await api.put(`/admin/users/${target.id}`, { can_edit_curriculum: next });
      setUsers((us) => us.map((u) => (u.id === target.id ? { ...u, can_edit_curriculum: data.can_edit_curriculum } : u)));
      toast.success(next ? `Granted curriculum edit to ${target.name}` : `Revoked curriculum edit from ${target.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change permission");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/users/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `roster-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Roster exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not export CSV");
    }
  };

  const saveClassName = async () => {
    const name = nameDraft.trim();
    if (!name) {
      toast.error("Class name can't be empty");
      return;
    }
    if (name === classInfo?.class_name) {
      setEditingName(false);
      return;
    }
    setClassBusy(true);
    try {
      const { data } = await api.put("/class/info", { class_name: name });
      setClassInfo(data);
      setEditingName(false);
      toast.success("Class name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update class name");
    } finally {
      setClassBusy(false);
    }
  };

  const regenerateCode = async () => {
    if (!window.confirm("Generate a new class code? The old invite + parent links will stop working.")) return;
    setClassBusy(true);
    try {
      const { data } = await api.put("/class/info", { regenerate_code: true });
      setClassInfo(data);
      toast.success("New class code generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate code");
    } finally {
      setClassBusy(false);
    }
  };

  const createParentNote = async (message, publish_at) => {
    try {
      const { data } = await api.post("/parent-notes", { message, publish_at });
      setParentNotes([data, ...parentNotes]);
      toast.success(data.scheduled ? "Note scheduled" : "Note posted to parents");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post note");
    }
  };

  const deleteParentNote = async (id) => {
    try {
      await api.delete(`/parent-notes/${id}`);
      setParentNotes(parentNotes.filter((a) => a.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete note");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-slate-500">
              <ShieldCheck weight="fill" className="text-yellow-500" size={18} />
              <span className="text-xs font-bold tracking-wider uppercase">Admin</span>
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight">Account management</h1>
            <p className="text-slate-600 mt-1">Create accounts without real emails, manage roles, reset passwords, and drill into student progress.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isSuperAdmin && (
              <button
                onClick={() => setDiagOpen(true)}
                data-testid="admin-diagnostics-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-outfit font-semibold hover:bg-slate-50"
                title="System diagnostics"
              >
                <Stethoscope size={18} weight="bold" /> Diagnostics
              </button>
            )}
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-outfit font-semibold hover:bg-slate-50"
              title="Download roster as CSV"
            >
              <DownloadSimple size={18} weight="bold" /> Export CSV
            </button>
            <Link
              to="/admin/print"
              data-testid="admin-print-handouts-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-outfit font-semibold hover:bg-slate-50"
              title="Print one handout per student with their QR code"
            >
              <Printer size={18} weight="bold" /> Print handouts
            </Link>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="admin-bulk-add-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-outfit font-semibold hover:bg-slate-50"
              title="Paste a list of names to create many accounts at once"
            >
              <UsersFour size={18} weight="bold" /> Bulk add
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="admin-add-user-btn"
              className="btn-tactile-yellow inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-400 text-yellow-950 font-outfit font-bold hover:bg-yellow-500"
            >
              <Plus size={18} weight="bold" /> Add account
            </button>
          </div>
        </div>

        {latestCreds && <CreatedCredentials creds={latestCreds} onClose={() => setLatestCreds(null)} />}

        {/* Review queue — surfaces pending student submissions so teachers
            can verify in one place instead of clicking into every student. */}
        <ReviewQueueCard />

        <div className="flex flex-wrap gap-2">
          {["all", ...ROLES].map((r) => (
            <button
              key={r}
              onClick={() => setFilter(r)}
              data-testid={`filter-${r}`}
              className={`px-3 py-1.5 text-sm rounded-full capitalize ${
                filter === r ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"
              }`}
            >
              {r === "all" ? `All (${users.length})` : `${r} (${users.filter((u) => u.role === r).length})`}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-6 text-slate-500" data-testid="admin-loading">Loading accounts...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] font-bold tracking-wider uppercase text-slate-500">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Login</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Last login</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3 font-medium">{u.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.username || u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => onRoleChange(u.id, e.target.value)}
                          data-testid={`role-select-${u.id}`}
                          className={`text-xs font-semibold capitalize px-2 py-1 rounded-full border-0 ${ROLE_BADGE[u.role] || "bg-slate-100"}`}
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(u.last_login_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {u.role === "student" && (
                            <button
                              onClick={() => setDrilldownUserId(u.id)}
                              data-testid={`drilldown-btn-${u.id}`}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                              title="View progress"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          {u.role === "parent" && (
                            <button
                              onClick={() => setEditLinksParent(u)}
                              data-testid={`edit-links-btn-${u.id}`}
                              className={`p-1.5 rounded-lg ${
                                (u.linked_student_ids || []).length === 0
                                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                  : "text-slate-600 hover:bg-slate-100"
                              }`}
                              title={
                                (u.linked_student_ids || []).length === 0
                                  ? "No students linked yet — click to link"
                                  : `Linked to ${(u.linked_student_ids || []).length} student(s)`
                              }
                            >
                              <LinkSimple size={16} weight={(u.linked_student_ids || []).length === 0 ? "bold" : "regular"} />
                            </button>
                          )}
                          {isSuperAdmin && u.role === "teacher" && !u.is_admin && (
                            <button
                              onClick={() => toggleCurriculumEdit(u)}
                              data-testid={`toggle-edit-${u.id}`}
                              className={`p-1.5 rounded-lg ${
                                u.can_edit_curriculum
                                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  : "text-slate-500 hover:bg-slate-100"
                              }`}
                              title={u.can_edit_curriculum ? "Revoke curriculum edit" : "Grant curriculum edit"}
                            >
                              {u.can_edit_curriculum ? <PencilSimple size={16} weight="fill" /> : <PencilSimpleSlash size={16} />}
                            </button>
                          )}
                          <button
                            onClick={() => resetPassword(u)}
                            data-testid={`reset-pw-btn-${u.id}`}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                            title="Reset password"
                          >
                            <Key size={16} />
                          </button>
                          <button
                            onClick={() => deleteUser(u)}
                            data-testid={`delete-user-btn-${u.id}`}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"
                            title="Delete account"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 italic">No accounts in this view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Class Settings + Notes for Parents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
          {/* CLASS SETTINGS */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="class-settings-card">
            <div className="flex items-center gap-2 mb-4">
              <Gear weight="fill" className="text-slate-500" size={20} />
              <h3 className="font-outfit font-bold text-xl">Class settings</h3>
            </div>

            {!classInfo ? (
              <div className="text-sm text-slate-500" data-testid="class-settings-loading">Loading...</div>
            ) : (
              <div className="space-y-4">
                {/* Class name */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                    Class name
                  </div>
                  {editingName ? (
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveClassName();
                          if (e.key === "Escape") {
                            setNameDraft(classInfo.class_name);
                            setEditingName(false);
                          }
                        }}
                        data-testid="class-name-input"
                        className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                        placeholder="Alpha AI Discovery Club · Tema Community 25"
                      />
                      <button
                        onClick={saveClassName}
                        disabled={classBusy}
                        data-testid="class-name-save-btn"
                        className="btn-tactile-yellow px-3 py-2 rounded-xl bg-yellow-400 text-yellow-950 text-sm font-bold hover:bg-yellow-500 disabled:opacity-60"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => { setNameDraft(classInfo.class_name); setEditingName(false); }}
                        className="px-3 py-2 rounded-xl hover:bg-slate-100 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        className="flex-1 font-outfit font-bold text-lg text-slate-900 break-words"
                        data-testid="class-name-display"
                      >
                        {classInfo.class_name}
                      </span>
                      <button
                        onClick={() => setEditingName(true)}
                        data-testid="class-name-edit-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold hover:bg-slate-50"
                      >
                        <PencilSimple size={14} weight="bold" /> Edit
                      </button>
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    Shown on the dashboard header, parent portal, login screen, and printable handouts.
                  </div>
                </div>

                {/* Class code (readonly) + regenerate */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                    Class code
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="flex-1 font-outfit font-black text-xl tracking-wide bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"
                      data-testid="class-code-display"
                    >
                      {classInfo.class_code}
                    </span>
                    <button
                      onClick={regenerateCode}
                      disabled={classBusy}
                      data-testid="class-code-regen-btn"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
                      title="Regenerate code — old links will stop working"
                    >
                      <ArrowsClockwise size={14} weight="bold" /> New code
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    Powers the student invite link (<span className="font-mono">/join/{classInfo.class_code}</span>) and the parent portal (<span className="font-mono">/parents/{classInfo.class_code}</span>).
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* NOTES FOR PARENTS */}
          <div data-testid="parent-notes-section">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-3">
              <div className="flex items-start gap-2">
                <ChatCircleText weight="fill" className="text-blue-500 shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-blue-900">
                  <div className="font-semibold mb-0.5">Notes for parents</div>
                  These appear ONLY on the public parent portal under <em>"From the teacher"</em>. They're separate from the in-class Pinboard — students won't see these.
                </div>
              </div>
            </div>
            <Announcements
              items={parentNotes}
              isTeacher={true}
              onCreate={createParentNote}
              onDelete={deleteParentNote}
              title="Notes for parents"
              icon={ChatCircleText}
              iconClass="text-blue-500"
              emptyHint="Post the first one!"
            />
          </div>
        </div>
      </main>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(data) => { setLatestCreds(data); load(); }}
        students={students}
      />
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCreated={load}
      />
      <EditLinksDialog
        parent={editLinksParent}
        students={students}
        onClose={() => setEditLinksParent(null)}
        onSaved={load}
      />
      <StudentProgressDrilldown userId={drilldownUserId} onClose={() => setDrilldownUserId(null)} />
      <DiagnosticsPanel open={diagOpen} onClose={() => setDiagOpen(false)} />
    </div>
  );
}

function DiagnosticsPanel({ open, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setData(null);
    setErr("");
    api.get("/admin/diagnostics")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        data-testid="diagnostics-overlay"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">System health</div>
              <h2 className="font-outfit text-2xl font-bold">Diagnostics</h2>
              <p className="text-xs text-slate-500 mt-1">Secrets are masked — only the presence of env vars is shown.</p>
            </div>
            <button
              onClick={onClose}
              data-testid="diagnostics-close"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="overflow-y-auto p-6 space-y-4">
            {err && <div className="text-red-600 text-sm" data-testid="diagnostics-error">{err}</div>}
            {!data && !err && <div className="text-slate-500 text-sm">Gathering diagnostics...</div>}
            {data && (
              <>
                <DiagSection title="Users" testid="diag-users">
                  <DiagRow k="Total" v={data.users.total} />
                  {Object.entries(data.users.by_role).map(([role, n]) => (
                    <DiagRow key={role} k={`Role: ${role}`} v={n} />
                  ))}
                </DiagSection>

                <DiagSection title="Demo accounts" testid="diag-demo">
                  <DiagRow
                    k="teacher@aiclub.gh"
                    v={data.demo_accounts["teacher@aiclub.gh"] ? "✓ present" : "✗ missing"}
                    bad={!data.demo_accounts["teacher@aiclub.gh"]}
                  />
                  <DiagRow
                    k="student@aiclub.gh"
                    v={data.demo_accounts["student@aiclub.gh"] ? "✓ present" : "✗ missing"}
                    bad={!data.demo_accounts["student@aiclub.gh"]}
                  />
                </DiagSection>

                <DiagSection title="Content" testid="diag-content">
                  <DiagRow k="Weeks seeded" v={data.content.weeks_seeded} bad={data.content.weeks_seeded !== 10} />
                  <DiagRow k="Progress entries" v={data.content.progress_entries} />
                  <DiagRow k="Announcements" v={data.content.announcements} />
                  <DiagRow k="Class name" v={data.content.class_name || "(unset)"} />
                  <DiagRow k="Class code" v={data.content.class_code || "(unset)"} />
                  <DiagRow k="Current week" v={data.content.current_week} />
                  <DiagRow k="Exhibition date" v={data.content.exhibition_date || "(not scheduled)"} />
                </DiagSection>

                <DiagSection title="Environment" testid="diag-env">
                  <DiagRow k="MONGO_URL set" v={data.env.MONGO_URL_set ? "✓" : "✗"} bad={!data.env.MONGO_URL_set} />
                  <DiagRow k="DB_NAME" v={data.env.DB_NAME || "(unset)"} />
                  <DiagRow k="JWT_SECRET set" v={data.env.JWT_SECRET_set ? "✓" : "✗"} bad={!data.env.JWT_SECRET_set} />
                  <DiagRow k="ADMIN_PASSWORD set" v={data.env.ADMIN_PASSWORD_set ? "✓" : "✗"} />
                  <DiagRow k="ADMIN_EMAIL" v={data.env.ADMIN_EMAIL_value} />
                  <DiagRow k="CORS_ORIGINS" v={data.env.CORS_ORIGINS} />
                </DiagSection>

                <div className="text-[11px] text-slate-400 text-right">
                  Server time: {new Date(data.server_time).toLocaleString()}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function DiagSection({ title, testid, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <div className="font-outfit font-bold text-sm mb-2">{title}</div>
      <dl className="divide-y divide-slate-100">{children}</dl>
    </div>
  );
}

function DiagRow({ k, v, bad }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <dt className="text-slate-600">{k}</dt>
      <dd className={`font-mono text-xs ${bad ? "text-red-600 font-semibold" : "text-slate-800"}`}>{String(v)}</dd>
    </div>
  );
}
